import { Box, CircularProgress, LinearProgress } from "@mui/material";
import { useEffect } from "react";
import CaptureView from "../../components/Captures/CaptureView";
import { useListCaptures } from "../../hooks/Capture/useListCaptures";
import { useErrorHandler } from "../../hooks/Error/useErrorHandler";

const ListCaptures = () => {
  const handleError = useErrorHandler();
  const {
    page,
    rowsPerPage,
    setPage,
    setRowsPerPage,
    setFilters,
    filters,
    capturesQuery,
    mapQuery,
    entitiesQuery,
    managementUnitsQuery,
  } = useListCaptures();

  const captures = capturesQuery.data?.data ?? [];
  const totalCapturesCount = capturesQuery.data?.total ?? 0;

  const hasMapFilters = !!(
    filters.entityId ||
    filters.managementUnitId ||
    filters.militaryMap ||
    filters.search
  );

  const geojson = hasMapFilters ? mapQuery.data : undefined;
  const entities = entitiesQuery.data ?? [];
  const allManagementUnits = managementUnitsQuery.data ?? [];

  useEffect(() => {
    if (capturesQuery.isError) handleError(capturesQuery.error);
    if (mapQuery.isError) handleError(mapQuery.error);
    if (entitiesQuery.isError) handleError(entitiesQuery.error);
    if (managementUnitsQuery.isError) handleError(managementUnitsQuery.error);
  }, [
    capturesQuery.isError,
    mapQuery.isError,
    entitiesQuery.isError,
    managementUnitsQuery.isError,
  ]);

  const mapHasData = geojson !== undefined && (geojson.features?.length ?? 0) > 0;

  const isInitialLoading =
    (capturesQuery.isPending && !capturesQuery.data) ||
    (entitiesQuery.isPending && !entitiesQuery.data) ||
    (managementUnitsQuery.isPending && !managementUnitsQuery.data);

  if (isInitialLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const isBackgroundFetching =
    (capturesQuery.isFetching && !capturesQuery.isPending) ||
    (mapQuery.isFetching && !mapQuery.isPending) ||
    (entitiesQuery.isFetching && !entitiesQuery.isPending) ||
    (managementUnitsQuery.isFetching && !managementUnitsQuery.isPending);

  const handleRequestRefresh = () => {
    capturesQuery.refetch();
    mapQuery.refetch();
    entitiesQuery.refetch();
    managementUnitsQuery.refetch();
  };

  return (
    <Box sx={{ position: "relative" }}>
      {isBackgroundFetching && (
        <LinearProgress
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 10,
            height: 2,
          }}
        />
      )}

      <CaptureView
        tableData={captures}
        entities={entities}
        allManagementUnits={allManagementUnits}
        onRequestRefresh={handleRequestRefresh}
        onApplyFilters={setFilters}
        geojson={geojson}
        mapHasData={mapHasData}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCapturesCount={totalCapturesCount}
        onPageChange={(_e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(Number(e.target.value));
          setPage(0);
        }}
        isLoading={false}
      />
    </Box>
  );
};

export default ListCaptures;